import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.js?url'
import { isEmpty, isNil } from 'ramda'
import { useMemo, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import { useTheme } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { Flex, FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { FullScreenModalLayout } from '@/@bothrs-ui/web/full-screen-modal-layout/FullScreenModalLayout'
import { NativePlatform, WebPlatform } from '@/capacitor/components/native-platform/NativePlatform'
import { breakpoints } from '@/config/layout.js'
import { mixpanel } from '@/mixpanel.js'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { DocumentMoreInfoModal } from '../../components/document-more-info-modal/DocumentMoreInfoModal.js'
import { StFooter, StFooterContent, StHeader, StHeaderContent } from './PdfPreview.styled'

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl

type Props = {
  filename: string
  preview?: string | Blob
  loading?: boolean
  downloadEnabled?: boolean
  moreInfoButton?: boolean
  finalDocument?: boolean
  onClose: () => void
  onAccept?: () => void
}

export const PdfPreview = ({
  filename,
  preview,
  loading,
  downloadEnabled,
  moreInfoButton,
  onAccept,
  onClose,
  finalDocument,
}: Props) => {
  const theme = useTheme()
  const isLoggedInForMandatee = useLoggedInForMandatee()
  const screenWidth = window.innerWidth

  const [showMoreInfo, setShowMoreInfo] = useState<boolean>(false)
  const [numberPages, setNumberPages] = useState<number>(0)

  // Convert base64 string to data URL if needed
  const previewData = useMemo((): string | Blob | undefined => {
    if (!preview) return undefined

    // If it's already a Blob, return as is
    if (preview instanceof Blob) return preview

    // If it's already a data URL, return as is
    if (typeof preview === 'string' && preview.startsWith('data:')) {
      return preview
    }

    // If it's a URL, return as is
    if (typeof preview === 'string' && (preview.startsWith('http') || preview.startsWith('blob:'))) {
      return preview
    }

    // If it's a base64 string, convert to data URL
    if (typeof preview === 'string') {
      return `data:application/pdf;base64,${preview}`
    }

    return preview
  }, [preview])

  const isMobile = useMediaQuery(breakpoints.toS)
  const transformConfig = isMobile
    ? {
        minScale: 0.75,
        maxScale: 2.5,
        initialScale: 1,
      }
    : {
        minScale: 0.25,
        maxScale: 1.5,
        initialScale: 0.5,
      }

  // Functions
  // ------------------------------------------------------------------------- /
  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumberPages(numPages)
  }

  const handleShare = async () => {
    if (!previewData) return

    try {
      // Share the actual PDF file, not the in-app route URL. previewData can be a
      // Blob or a data/blob/http URL; fetch normalises all of them to a Blob.
      const blob = previewData instanceof Blob ? previewData : await (await fetch(previewData)).blob()
      const file = new File([blob], `${filename}.pdf`, { type: 'application/pdf' })

      if (!navigator.canShare?.({ files: [file] })) {
        console.info('Sharing PDF not supported')
        return
      }

      await navigator.share({
        title: t({ id: 'share.pdf', message: 'PDF delen.' }),
        text: t({ id: 'share.pdf.message', message: 'Deel deze PDF' }),
        files: [file],
      })
    } catch (error) {
      console.error('Error sharing:', error)
    }
  }

  // Render
  // ------------------------------------------------------------------------- /
  const content = loading ? (
    <AnimatedLoader />
  ) : (
    <Flex
      justifyContent='center'
      style={{
        maxWidth: '100%',
        width: '100%',
        height: '100%',
        maxHeight: 'calc(100vh - 64px + var(--safe-area-inset-top))',
      }}
    >
      <TransformWrapper {...transformConfig}>
        <TransformComponent>
          <Document file={previewData} onLoadSuccess={handleDocumentLoadSuccess}>
            {Array.from({ length: numberPages }, (_, index) => (
              <Page
                key={`page-${index + 1}`}
                pageNumber={index + 1}
                className={'pdf-page'}
                renderTextLayer={false}
                renderForms={false}
                renderAnnotationLayer={false}
                width={screenWidth}
              />
            ))}
          </Document>
        </TransformComponent>
      </TransformWrapper>
    </Flex>
  )

  const header = (
    <StHeader>
      <StHeaderContent>
        <FlexColumn>
          <Span type='BodyMediumBold' color={theme.Colors.Neutrals.Darker}>
            {filename}
          </Span>
          <Span type='BodySmallRegular' color={theme.Colors.Neutrals.Dark}>
            {`${numberPages} paginas`}
          </Span>
        </FlexColumn>
        <Flex gap={theme.UI.Spacing.Space2} alignItems='center' onClick={onClose} style={{ cursor: 'pointer' }}>
          <FontAwesomeIcon icon={['far', 'close']} color={theme.Colors.Neutrals.Darkest} />
          <Span type='BodyMediumBold' color={theme.Colors.Neutrals.Darkest}>
            {t({ id: 'document.pdf.close', message: 'Sluiten' })}
          </Span>
        </Flex>
      </StHeaderContent>
    </StHeader>
  )

  const footer =
    downloadEnabled || onAccept ? (
      <StFooter>
        <StFooterContent gap={theme.UI.Spacing.Space3}>
          {onAccept ? (
            <Button
              variant='Primary'
              onClick={() => {
                if (finalDocument)
                  mixpanel.track('document_signing_review_sign', {
                    is_mandate: isLoggedInForMandatee,
                  })
                else
                  mixpanel.track('document_signing_review_approve_document', {
                    is_mandate: isLoggedInForMandatee,
                  })
                onAccept()
              }}
              prefixIcon={finalDocument ? ['fal', 'signature'] : ['fal', 'check']}
            >
              {finalDocument
                ? t({
                    id: 'document.agree-and-sign',
                    message: 'Akkoord en onderteken',
                  })
                : t({ id: 'document.agree', message: 'Ik ga akkoord' })}
            </Button>
          ) : null}
          {moreInfoButton ? (
            <Button
              variant='Secondary'
              onClick={() => {
                mixpanel.track('document_signing_review_questions', {
                  is_mandate: isLoggedInForMandatee,
                })
                setShowMoreInfo(true)
              }}
              prefixIcon={['fal', 'info-circle']}
            >
              {t({ id: 'document.questions', message: 'Ik heb nog vragen' })}
            </Button>
          ) : null}

          {downloadEnabled ? (
            <>
              <NativePlatform>
                <Button variant='Secondary' onClick={handleShare} prefixIcon={['far', 'share-from-square']}>
                  {t({ id: 'document.pdf.share', message: 'Delen' })}
                </Button>
              </NativePlatform>
              <WebPlatform>
                <a
                  href={previewData instanceof Blob ? URL.createObjectURL(previewData) : (previewData as string)}
                  download={`${filename}.pdf`}
                >
                  <Button variant='Secondary' prefixIcon={['fal', 'download']}>
                    {t({
                      id: 'document.pdf.download',
                      message: 'Download PDF',
                    })}
                  </Button>
                </a>
              </WebPlatform>
            </>
          ) : null}
        </StFooterContent>
      </StFooter>
    ) : null

  return (
    <FullScreenModalLayout
      isOpen={!isNil(previewData) && !isEmpty(previewData)}
      backgroundColor={theme.Colors.Background.Light}
      header={header}
      footer={footer}
    >
      {content}
      <DocumentMoreInfoModal
        isVisible={showMoreInfo}
        onClose={() => setShowMoreInfo(false)}
        onPhoneClick={() => mixpanel.track('appointment_contact', { type: 'phone', origin: 'detail' })}
      />
    </FullScreenModalLayout>
  )
}
